import { Info, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Progress } from "@/components/ui/progress";
import type { KpsRecalculationRun } from "@/types";

export interface KpsRecalculationToastProps {
  run: KpsRecalculationRun;
  onDismiss: () => void;
}

// AUT-281: PR #259 rewrote `RECALC_ALL_KPS` into one atomic set-based UPDATE -- the pending-count
// signal the percentage was derived from no longer exists, and the run is now fast enough (~8.7s at
// ~600k rows) that any percentage/ETA we could show would be fabricated, which is the exact defect
// AUT-282 just fixed. `percent_complete` and `estimated_seconds_remaining` stay on the wire
// (`src/types/kps-recalculation.ts`) but are permanently null; the toast no longer reads them and
// shows elapsed time instead. Three full stops, never `...` (the ellipsis glyph), matching the
// existing copy in this module (`KPSConfigurationTab.tsx`, `LandscapeConfigurationTab.tsx`,
// `NotificationsTab.tsx`).
export function formatKpsRecalculationElapsed(
  run: Pick<KpsRecalculationRun, "started_at">,
  nowMs: number
): string {
  if (run.started_at === null) {
    return "Starting...";
  }

  const startedMs = new Date(run.started_at).getTime();
  // A `started_at` in the future (clock skew) must never produce a negative elapsed time.
  const elapsedSeconds = Math.max(0, Math.floor((nowMs - startedMs) / 1000));

  if (elapsedSeconds < 60) {
    return `Running for ${elapsedSeconds}s`;
  }

  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return `Running for ${minutes}m ${seconds}s`;
}

// `design-input/kps-config-recalculation/sources.md`, screenshot `02-progress-toast.png` -- icon,
// title, body, progress bar, one elapsed-time line, `x` dismiss. Rendered through `toast.custom` by
// `useKpsRecalculationToast`, on the single existing `<Sonner />` mount (`constraints.md § C-28`).
//
// The bar is always indeterminate (AUT-281): there is no meaningful percentage to show any more, so
// this never branches on `run.percent_complete`. Elapsed time ticks every second from a local timer
// -- the poll is every 5s, and a counter jumping in 5-second steps reads as broken.
export function KpsRecalculationToast({ run, onDismiss }: KpsRecalculationToastProps) {
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const elapsedText = formatKpsRecalculationElapsed(run, nowMs);

  return (
    <div
      data-testid="kps-recalculation-toast"
      className="relative w-full max-w-sm overflow-hidden rounded-lg border border-border bg-background shadow-lg"
    >
      <div className="absolute inset-y-0 left-0 w-1 bg-primary" aria-hidden="true" />
      <div className="flex items-start gap-2 py-3 pl-4 pr-3">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">KPS recalculation in progress</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Recalculating the KPS for every keyword in Signal. You can keep working while this
            runs.
          </p>
          <Progress
            value={100}
            indicatorClassName="animate-pulse"
            className="mt-3 h-2"
            aria-label="KPS recalculation progress"
          />
          <div className="mt-2 text-xs text-muted-foreground">
            <span data-testid="kps-recalculation-toast-elapsed">{elapsedText}</span>
          </div>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
